/**
 * LoginPage — Regional Admin Authentication
 *
 * Select a region and enter password to access the dashboard.
 * Super Admin (Global) option available for full access.
 */

import { useState, useEffect } from 'react';
import { clearAuthSession, readAuthSession, saveAuthSession } from '../../utils/authSession';

const API = process.env.REACT_APP_ACQUISITION_API || 'http://localhost:5500';
const LOCAL_TRUSTED_DEVICE = process.env.NODE_ENV === 'development'
    && ['127.0.0.1', 'localhost'].includes(window.location.hostname);

interface AuthInfo {
    token: string;
    role: string;
    regions: string[];
    label: string;
}

interface LoginPageProps {
    onLogin: (auth: AuthInfo) => void;
}

const REGION_OPTIONS = [
    { key: 'global', label: "🌐 超级管理员（全部地区）", icon: '🔑', color: '#a855f7' },
    { key: 'us', label: "🌎 北美洲", icon: '🇺🇸', color: '#3b82f6' },
    { key: 'lat', label: "🌎 拉丁美洲", icon: '🌎', color: '#f59e0b' },
    { key: 'eu', label: "🌍 欧洲", icon: '🇪🇺', color: '#6366f1' },
    { key: 'asia', label: "🌏 亚太地区与中东", icon: '🌏', color: '#ec4899' },
    { key: 'af', label: "🌍 非洲", icon: '🌍', color: '#22c55e' },
];

export function LoginPage({ onLogin }: LoginPageProps) {
    const [selectedRegion, setSelectedRegion] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [localLoginLoading, setLocalLoginLoading] = useState(LOCAL_TRUSTED_DEVICE);
    const [localLoginError, setLocalLoginError] = useState('');
    const [localLoginAttempt, setLocalLoginAttempt] = useState(0);

    // Try to restore session from localStorage
    useEffect(() => {
        let active = true;

        async function restoreOrCreateSession() {
            const saved = readAuthSession(API);
            if (saved) {
                try {
                    const auth: AuthInfo = saved;
                    const verification = await fetch(`${API}/auth/verify`, {
                        headers: { Authorization: `Bearer ${auth.token}` },
                    });
                    const data = await verification.json();
                    if (data.valid) {
                        saveAuthSession(auth, API);
                        if (active) onLogin(auth);
                        return;
                    }
                } catch {
                    // Fall through to local trusted-device login or the normal form.
                }
                clearAuthSession(API);
            }

            if (!LOCAL_TRUSTED_DEVICE) return;

            if (active) {
                setLocalLoginLoading(true);
                setLocalLoginError('');
            }

            try {
                const response = await fetch('/__lawhelper/local-admin-session', {
                    method: 'POST',
                    headers: { 'X-Lawhelper-Local-Device': '1' },
                });
                const contentType = response.headers.get('content-type') || '';
                if (!contentType.includes('application/json')) {
                    throw new Error('本地认证接口尚未就绪，请刷新页面后重试。');
                }
                const data = await response.json();
                if (!response.ok) throw new Error(data.error || "本机可信设备登录失败。");
                if (data.role !== 'admin-global' || typeof data.token !== 'string') {
                    throw new Error("本机管理员登录状态无效。");
                }

                const auth: AuthInfo = {
                    token: data.token,
                    role: data.role,
                    regions: data.regions,
                    label: data.label,
                };
                saveAuthSession(auth, API);
                if (active) onLogin(auth);
            } catch (localError) {
                if (active) {
                    setLocalLoginError(localError instanceof Error ? localError.message : "本机登录失败。");
                }
            } finally {
                if (active) setLocalLoginLoading(false);
            }
        }

        restoreOrCreateSession();
        return () => {
            active = false;
        };
    }, [onLogin, localLoginAttempt]);

    async function handleLogin(e: React.FormEvent) {
        e.preventDefault();
        if (!selectedRegion || !password) return;

        setLoading(true);
        setError('');

        try {
            const res = await fetch(`${API}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ region: selectedRegion, password }),
            });

            const data = await res.json();

            if (!res.ok) {
                setError(data.error || "登录失败");
                return;
            }

            const auth: AuthInfo = {
                token: data.token,
                role: data.role,
                regions: data.regions,
                label: data.label,
            };

            saveAuthSession(auth, API);
            onLogin(auth);
        } catch (err) {
            setError("无法连接服务器，请稍后重试。");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #1e1b4b 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        }}>
            <div style={{
                width: '100%',
                maxWidth: 420,
                padding: 40,
                background: 'rgba(15, 23, 42, 0.8)',
                backdropFilter: 'blur(20px)',
                borderRadius: 20,
                border: '1px solid rgba(99, 102, 241, 0.2)',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
            }}>
                {/* Logo / Title */}
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <div style={{
                        fontSize: 40,
                        marginBottom: 12,
                    }}>🚀</div>
                    <h1 style={{
                        margin: 0,
                        fontSize: 22,
                        fontWeight: 800,
                        color: '#e2e8f0',
                        letterSpacing: '-0.02em',
                    }}>Acquisition Engine</h1>
                    <p style={{
                        margin: '6px 0 0',
                        fontSize: 13,
                        color: '#64748b',
                    }}>地区管理员登录</p>
                </div>

                {LOCAL_TRUSTED_DEVICE ? (
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            padding: '18px 16px',
                            marginBottom: 16,
                            background: 'rgba(56, 189, 248, 0.08)',
                            border: '1px solid rgba(56, 189, 248, 0.25)',
                            borderRadius: 12,
                            color: '#bae6fd',
                            fontSize: 13,
                            lineHeight: 1.6,
                        }}>
                            {localLoginLoading
                                ? '正在连接本机可信设备…'
                                : localLoginError || '正在创建管理员会话…'}
                        </div>
                        {!localLoginLoading && localLoginError && (
                            <button
                                type="button"
                                onClick={() => setLocalLoginAttempt((attempt) => attempt + 1)}
                                style={{
                                    width: '100%',
                                    padding: 14,
                                    background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 12,
                                    cursor: 'pointer',
                                    fontWeight: 700,
                                    fontSize: 15,
                                }}
                            >
                                重试本机登录
                            </button>
                        )}
                    </div>
                ) : <form onSubmit={handleLogin}>
                    {/* Region Selection */}
                    <label style={{
                        display: 'block',
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        marginBottom: 8,
                    }}>选择地区</label>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 8,
                        marginBottom: 20,
                    }}>
                        {REGION_OPTIONS.map((r) => (
                            <button
                                key={r.key}
                                type="button"
                                onClick={() => setSelectedRegion(r.key)}
                                style={{
                                    padding: r.key === 'global' ? '12px 14px' : '10px 14px',
                                    background: selectedRegion === r.key ? `${r.color}22` : 'rgba(30, 41, 59, 0.5)',
                                    border: `1.5px solid ${selectedRegion === r.key ? r.color : '#334155'}`,
                                    borderRadius: 10,
                                    color: selectedRegion === r.key ? r.color : '#94a3b8',
                                    cursor: 'pointer',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    textAlign: 'left',
                                    transition: 'all 0.15s ease',
                                    gridColumn: r.key === 'global' ? '1 / -1' : undefined,
                                }}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>

                    {/* Password */}
                    <label style={{
                        display: 'block',
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        marginBottom: 8,
                    }}>密码</label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="输入管理员密码"
                        autoFocus
                        style={{
                            width: '100%',
                            padding: '12px 16px',
                            background: '#0a0f1e',
                            border: '1.5px solid #334155',
                            borderRadius: 10,
                            color: '#e2e8f0',
                            fontSize: 14,
                            boxSizing: 'border-box',
                            fontFamily: 'inherit',
                            marginBottom: error ? 8 : 20,
                        }}
                    />

                    {/* Error */}
                    {error && (
                        <p style={{
                            margin: '0 0 16px',
                            padding: '8px 12px',
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: 8,
                            color: '#f87171',
                            fontSize: 12,
                            fontWeight: 600,
                        }}>⚠️ {error}</p>
                    )}

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={loading || !selectedRegion || !password}
                        style={{
                            width: '100%',
                            padding: 14,
                            background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 12,
                            cursor: 'pointer',
                            fontWeight: 700,
                            fontSize: 15,
                            opacity: loading || !selectedRegion || !password ? 0.5 : 1,
                            transition: 'opacity 0.2s',
                        }}
                    >
                        {loading ? "⏳ 正在登录…" : "🔐 登录"}
                    </button>
                </form>}
            </div>
        </div>
    );
}
